import React, { useState, useEffect, useMemo } from 'react';
import { X, Send, Bot, Mail, CheckCircle, AlertCircle, Building2, Loader2 } from 'lucide-react';
import { sendAsDavid, sendRecommendationEmail } from '../services/recommendationsApi';
import { CandidateMatchResult, JobSummary } from '../services/jobMatchingApi';
import { normalizeMatchScore } from '../../shared/utils/apiClient';
import {
    fetchCompanyById,
    sendAsDavidToCompany,
    draftCandidateToRecommendation,
    DraftCandidate,
    CompanyDetail,
} from '../services/davidInboxApi';

// SendMessageModal supports 3 modes:
//
//   'david'   — (legacy) Send a David message to a single candidate with
//               a job recommendation attached. Used by Recommend Jobs flow.
//
//   'email'   — Email one imported/SE candidate about a job match. Template
//               is pre-filled, admin refines before sending.
//
//   'company' — Send a David message to the COMPANY OWNER of the selected
//               job recommending one or more on-platform candidates. Pre-
//               fills a draft template listing every candidate with their
//               signals. Supports single-row OR bulk selection.

interface SendMessageModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Single candidate — used for david/email modes and single-row company sends. */
    candidate: CandidateMatchResult | null;
    /** Optional bulk list — when provided in 'company' mode, overrides `candidate`
     *  and sends all listed candidates in one David message. */
    candidates?: CandidateMatchResult[];
    job: JobSummary | null;
    mode: 'david' | 'email' | 'company';
    onSuccess?: () => void;
}

const SendMessageModal: React.FC<SendMessageModalProps> = ({
    isOpen,
    onClose,
    candidate,
    candidates,
    job,
    mode,
    onSuccess,
}) => {
    // Normalize: in company mode, use the `candidates` array if provided, else
    // fall back to a single-element array built from `candidate`.
    const targetCandidates: CandidateMatchResult[] = useMemo(() => {
        if (mode === 'company' && candidates && candidates.length > 0) return candidates;
        if (candidate) return [candidate];
        return [];
    }, [mode, candidates, candidate]);

    const firstCandidate = targetCandidates[0] || null;
    const candidateName = firstCandidate?.candidate.name || 'Candidate';
    const firstName = candidateName.split(' ')[0];
    const jobTitle = job?.title || 'the position';
    const companyName = job?.companyName || 'the company';
    const jobLink = job?.id ? `https://www.hushtalent.io/candidate/jobs?apply=${job.id}` : '';

    // Company owner resolution — only needed for 'company' mode
    const [companyDetail, setCompanyDetail] = useState<CompanyDetail | null>(null);
    const [resolvingCompany, setResolvingCompany] = useState(false);

    useEffect(() => {
        if (!isOpen || mode !== 'company' || !job?.companyId) {
            setCompanyDetail(null);
            return;
        }
        setResolvingCompany(true);
        fetchCompanyById(job.companyId)
            .then((c) => setCompanyDetail(c))
            .finally(() => setResolvingCompany(false));
    }, [isOpen, mode, job?.companyId]);

    // Draft-text builders — one per mode
    const buildDavidDraft = (): string =>
        `Hi ${firstName}!\n\nWe found a great opportunity for you — ${jobTitle} at ${companyName}. Based on your skills and experience, we think you'd be an excellent fit!\n\nCheck it out and apply here: ${jobLink}\n\nBest,\nThe Hush Team`;

    const buildEmailDraft = (): string => {
        const location = firstCandidate?.candidate.locations?.[0] || '';
        const locationLine = location ? ` in ${location}` : '';
        return `Hi ${firstName},\n\nWe came across your profile and thought you'd be a great match for a role we're helping fill:\n\n  ${jobTitle} at ${companyName}${locationLine}\n\nIf this looks interesting, reply to this email and we'll share the full job details and connect you with the hiring team.\n\nBest,\nThe Hush Team`;
    };

    // Company mode draft template — intro + closing only.
    //
    // The candidate details (name, title, years, skills, qwen, matched
    // signals) render as rich clickable cards in the recipient's David
    // inbox (Phase 3 company-facing UI) and in the admin History tab.
    // Duplicating them as bullet prose in the message body would make
    // both views noisy and harder to scan — the cards ARE the content.
    //
    // The admin can still edit this prose freely before sending.
    const buildCompanyDraft = (ownerFirstName: string, cands: CandidateMatchResult[]): string => {
        const salutation = ownerFirstName ? `Hi ${ownerFirstName},` : 'Hi there,';
        const roleAtCompany = `${jobTitle}${job?.companyName ? ` at ${job.companyName}` : ''}`;

        const intro =
            cands.length === 1
                ? `I've found a strong candidate match for your ${roleAtCompany} role. Take a look at the profile below — happy to set up an introduction whenever you're ready.`
                : `I've lined up ${cands.length} strong candidates for your ${roleAtCompany} role. The full profiles are below — let me know which ones you'd like me to connect you with.`;

        const closing = `\n\nBest,\nDavid`;
        return `${salutation}\n\n${intro}${closing}`;
    };

    const getDefaultMessage = (): string => {
        if (mode === 'david') return buildDavidDraft();
        if (mode === 'email') return buildEmailDraft();
        if (mode === 'company') {
            const ownerFirst = companyDetail?.owner?.name?.split(' ')[0] || '';
            return buildCompanyDraft(ownerFirst, targetCandidates);
        }
        return '';
    };

    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [errorMsg, setErrorMsg] = useState('');

    // Refresh the draft whenever the modal opens, the target candidate(s)
    // change, or the resolved company changes (company owner resolution is
    // async so the salutation can only be computed after it lands).
    useEffect(() => {
        if (!isOpen) return;
        setMessage(getDefaultMessage());
        setStatus('idle');
        setErrorMsg('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, firstCandidate?.candidate.id, targetCandidates.length, companyDetail?.id, mode]);

    if (!isOpen || !firstCandidate || !job) return null;

    const handleSend = async () => {
        setSending(true);
        setStatus('idle');
        try {
            if (mode === 'david') {
                const matchScore = normalizeMatchScore(
                    firstCandidate.vectorSimilarity,
                    firstCandidate.qwenScore,
                    firstCandidate.softCriteriaScore
                );
                await sendAsDavid({
                    target_user_id: firstCandidate.candidate.id,
                    content: message,
                    job_recommendations: [
                        {
                            job_id: job.id,
                            title: job.title,
                            company_name: job.companyName,
                            location: job.location,
                            employment_type: job.employmentType,
                            match_score: matchScore,
                        },
                    ],
                });
            } else if (mode === 'email') {
                await sendRecommendationEmail({
                    candidateEmail: firstCandidate.candidate.email,
                    candidateName,
                    jobs: [
                        {
                            title: job.title,
                            companyName: job.companyName,
                            location: job.location,
                            matchScore: firstCandidate.matchScore,
                        },
                    ],
                    message,
                });
            } else if (mode === 'company') {
                if (!companyDetail || !companyDetail.owner.userId) {
                    throw new Error("Company owner could not be resolved — can't send");
                }
                // Map CandidateMatchResult → DraftCandidate → CandidateRecommendation
                const recommendations = targetCandidates.map((m) => {
                    const draftLike: DraftCandidate = {
                        id: m.candidate.id,
                        name: m.candidate.name || 'Candidate',
                        titles: m.candidate.allJobTitles,
                        companies: m.candidate.allCompanies,
                        skills: m.candidate.skills,
                        locations: m.candidate.locations,
                        years_experience: m.candidate.yearsExp,
                        source: m.source,
                        match_score: m.matchScore,
                        vector_similarity: m.vectorSimilarity,
                        qwen_score: m.qwenScore,
                        soft_criteria_score: m.softCriteriaScore,
                    };
                    return draftCandidateToRecommendation(draftLike);
                });
                await sendAsDavidToCompany({
                    target_user_id: companyDetail.owner.userId,
                    target_role: 'company',
                    company_id: companyDetail.id,
                    company_name: companyDetail.name,
                    content: message,
                    candidate_recommendations: recommendations,
                    recruiter_query: `Candidates for ${job.title} at ${job.companyName}`,
                    original_draft: message,
                });
            }
            setStatus('success');
            onSuccess?.();
        } catch (err: any) {
            setStatus('error');
            setErrorMsg(err.message || 'Failed to send');
        } finally {
            setSending(false);
        }
    };

    const modeConfig = {
        david: { icon: Bot, label: 'Send as David', color: 'bg-[#015FA6]', hoverColor: 'hover:bg-[#014a85]' },
        email: { icon: Mail, label: 'Send Email', color: 'bg-amber-600', hoverColor: 'hover:bg-amber-700' },
        company: {
            icon: Send,
            label: `Recommend to ${companyDetail?.name || 'Company'}`,
            color: 'bg-emerald-600',
            hoverColor: 'hover:bg-emerald-700',
        },
    } as const;

    const config = modeConfig[mode];
    const ActionIcon = config.icon;

    const headerTitle =
        mode === 'company'
            ? `Recommend to ${companyDetail?.name || companyName}`
            : mode === 'email'
            ? 'Email Candidate'
            : 'Send David Message';

    const headerSubtitle =
        mode === 'company'
            ? targetCandidates.length === 1
                ? `Recommending ${candidateName} for ${jobTitle}`
                : `Recommending ${targetCandidates.length} candidates for ${jobTitle}`
            : `To: ${candidateName} · Re: ${jobTitle}`;

    const sendDisabled =
        sending ||
        !message.trim() ||
        (mode === 'company' && (!companyDetail || !companyDetail.owner.userId));

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div
                className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4"
                style={{ fontFamily: 'Manrope, sans-serif' }}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <div className="min-w-0 flex-1">
                        <h3 className="text-lg font-semibold text-[#0B111E] truncate">{headerTitle}</h3>
                        <p className="text-sm text-[#757575] mt-0.5 truncate">{headerSubtitle}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
                    >
                        <X size={18} className="text-[#757575]" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-4">
                    {/* Company mode: show owner resolution status */}
                    {mode === 'company' && (
                        <div className="mb-3">
                            {resolvingCompany && (
                                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600">
                                    <Loader2 size={12} className="animate-spin" />
                                    Resolving company owner…
                                </div>
                            )}
                            {!resolvingCompany && companyDetail?.owner.userId && (
                                <div className="flex items-start gap-2 px-3 py-2 bg-emerald-50 border border-emerald-100 rounded-lg text-xs text-emerald-900">
                                    <Building2 size={14} className="flex-shrink-0 mt-0.5" />
                                    <div className="min-w-0">
                                        <div className="font-medium truncate">{companyDetail.name}</div>
                                        <div className="text-emerald-700 truncate">
                                            Owner: {companyDetail.owner.name || 'Unknown'}
                                            {companyDetail.owner.email ? ` · ${companyDetail.owner.email}` : ''}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {!resolvingCompany && (!companyDetail || !companyDetail.owner.userId) && (
                                <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
                                    <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                                    <span>This company has no owner on file — cannot send David message.</span>
                                </div>
                            )}
                        </div>
                    )}

                    {status === 'success' ? (
                        <div className="text-center py-8">
                            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle size={24} className="text-green-600" />
                            </div>
                            <h4 className="text-lg font-semibold text-[#0B111E] mb-2">Sent Successfully</h4>
                            <p className="text-sm text-[#757575]">
                                {mode === 'david' && 'David message sent to candidate.'}
                                {mode === 'email' && 'Email sent to candidate.'}
                                {mode === 'company' &&
                                    `${targetCandidates.length} candidate${
                                        targetCandidates.length === 1 ? '' : 's'
                                    } recommended to ${companyDetail?.name || 'company'}.`}
                            </p>
                        </div>
                    ) : (
                        <>
                            <textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                rows={mode === 'company' ? 12 : 8}
                                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#0B111E] resize-none focus:outline-none focus:ring-2 focus:ring-[#015FA6]/20 focus:border-[#015FA6] font-mono"
                                placeholder="Write your message..."
                            />
                            {status === 'error' && (
                                <div className="flex items-center gap-2 mt-3 text-red-600 text-sm">
                                    <AlertCircle size={14} />
                                    {errorMsg}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-[#757575] hover:text-[#0B111E] hover:bg-gray-50 rounded-lg transition-colors"
                    >
                        {status === 'success' ? 'Close' : 'Cancel'}
                    </button>
                    {status !== 'success' && (
                        <button
                            onClick={handleSend}
                            disabled={sendDisabled}
                            className={`flex items-center gap-2 px-5 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${config.color} ${config.hoverColor}`}
                        >
                            {sending ? (
                                <>
                                    <Loader2 size={16} className="animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <ActionIcon size={16} />
                                    {config.label}
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SendMessageModal;
